import { api } from '@/config/api';

export interface IgAccountInfo {
  id: string;
  username: string;
  name?: string;
  biography?: string;
  website?: string;
  profile_picture_url?: string;
  followers_count?: number;
  follows_count?: number;
  media_count?: number;
  fetchedAt: string;
  fromCache: boolean;
}

export type IgMediaType = 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM' | 'REELS';

export interface IgMediaItem {
  id: string;
  media_type: IgMediaType;
  media_product_type?: string;
  media_url?: string;
  thumbnail_url?: string;
  /**
   * Permanent ImageKit-hosted mirror — prefer over `media_url`/`thumbnail_url`.
   * The IG-CDN URLs are signed and expire in a few hours, so anything that
   * caches them goes blank. Backend mirrors them on first fetch.
   */
  imagekit_url?: string;
  permalink?: string;
  caption?: string;
  timestamp?: string;
  like_count?: number;
  comments_count?: number;
  insights?: { data: Array<{ name: string; values: Array<{ value: number }> }> };
}

export interface IgMediaListResponse {
  data: IgMediaItem[];
  paging?: { cursors?: { before?: string; after?: string }; next?: string };
  fetchedAt: string;
  fromCache: boolean;
}

export interface IgInsightValue { value: number | Record<string, number>; end_time?: string; }
/**
 * `values` is optional on purpose — Meta omits it entirely on metrics that
 * report a single aggregate (`total_value`) instead of a time series.
 */
export interface IgInsightsMetric { name: string; period: string; values?: IgInsightValue[]; total_value?: { value: number }; title?: string; description?: string; }

export interface IgInsightsResponse {
  data: IgInsightsMetric[];
  fetchedAt: string;
  fromCache: boolean;
  error?: string;
}

export interface IgCarouselChild {
  image_url?: string;
  video_url?: string;
}

export interface IgUserTag {
  username: string;
  /** Required for IMAGE posts — relative position in [0, 1]. */
  x?: number;
  y?: number;
}

// Mirrors backend PublishPostDto. CAROUSEL_ALBUM and STORIES were already
// implemented server-side (container polling included) but unreachable because
// this type only allowed three of the five.
export interface PublishPostPayload {
  media_type: 'IMAGE' | 'VIDEO' | 'REELS' | 'CAROUSEL_ALBUM' | 'STORIES';
  image_url?: string;
  video_url?: string;
  cover_url?: string;
  /** CAROUSEL_ALBUM: 2–10 children, each an image or a video. */
  children?: IgCarouselChild[];
  caption?: string;
  share_to_feed?: boolean;
  location_id?: string;
  user_tags?: IgUserTag[];
  /** VIDEO only — thumbnail frame, ms from start. */
  thumb_offset?: number;
  collaborators?: string[];
}

export interface PublishResult {
  mediaId: string;
  permalink?: string;
  containerId: string;
  mediaType: string;
  publishedAt: string;
}

export interface IgMediaInsightsResponse {
  mediaId: string;
  metrics: Array<{ name: string; value: number; title?: string; description?: string }>;
  fetchedAt: string;
}

export const instagramService = {
  account: async (refresh = false): Promise<IgAccountInfo> =>
    (await api.get('/slighter_crm/facebook/instagram/account', { params: { refresh: refresh ? '1' : undefined } })).data,
  media: async (refresh = false, limit?: number, after?: string, expectedStatuses?: number[]): Promise<IgMediaListResponse> =>
    (await api.get('/slighter_crm/facebook/instagram/media', {
      params: { refresh: refresh ? '1' : undefined, limit, after },
      expectedStatuses,
    })).data,
  mediaInsights: async (mediaId: string): Promise<IgMediaInsightsResponse> =>
    (await api.get(`/slighter_crm/facebook/instagram/media/${mediaId}/insights`)).data,
  deleteMedia: async (mediaId: string): Promise<{ ok: true }> =>
    (await api.delete(`/slighter_crm/facebook/instagram/media/${mediaId}`)).data,
  insights: async (refresh = false, days?: number): Promise<IgInsightsResponse> =>
    (await api.get('/slighter_crm/facebook/instagram/insights', { params: { refresh: refresh ? '1' : undefined, days } })).data,
  publish: async (payload: PublishPostPayload): Promise<PublishResult> =>
    (await api.post('/slighter_crm/facebook/instagram/publish', payload, { timeout: 150_000 })).data,
};

export function getMediaInsight(media: IgMediaItem, name: string): number | null {
  const m = media.insights?.data?.find(d => d.name === name);
  if (!m || !m.values?.[0]) return null;
  const v = m.values[0].value;
  return typeof v === 'number' ? v : null;
}
